import katex from 'katex';
import 'katex/dist/katex.min.css';
import { espacerMilliersTexte, espacerMilliersTex } from '../../utils/milliers';

/**
 * Rendu intelligent texte / KaTeX.
 * Les espaces de milliers sont posés ici, à l'affichage (utils/milliers.js) :
 * 4512354 se lit 4 512 354, en texte comme dans une formule.
 *
 * Détecte les fragments `$...$` dans la chaîne et les rend en KaTeX inline.
 * Le texte hors des `$` est affiché normalement.
 *
 * Une formule n'est JAMAIS coupée (prof 2026-09-25, vu sur une réponse de QCM
 * où « ĈGM = » finissait une ligne et « ĈGL » commençait la suivante) : KaTeX
 * découpe une formule en plusieurs blocs au niveau des signes = et + pour
 * permettre la coupure, chaque fragment est donc enveloppé dans
 * `.expr-insecable` (white-space: nowrap, index.css), la règle déjà appliquée
 * aux énoncés par htmlAvecKatex. La ligne se coupe entre les formules, aux
 * espaces et aux virgules du texte, jamais au milieu d'une expression.
 *
 * Exemples :
 *   '$\\dfrac{3}{4}$'                         → fraction seule
 *   'Entre $\\dfrac{1}{9}$ et $\\dfrac{2}{9}$' → texte mixte avec fractions
 *   'texte normal'                              → texte brut (aucun $)
 */

const MATH_REGEX = /\$([^$]+)\$/g;

const MathText = ({ children, className }) => {
    if (typeof children !== 'string' || !children.includes('$')) {
        return <span className={className}>{espacerMilliersTexte(children)}</span>;
    }

    // Découper la chaîne en segments texte / math
    const parts = [];
    let lastIndex = 0;
    let match;
    const re = new RegExp(MATH_REGEX.source, 'g');

    while ((match = re.exec(children)) !== null) {
        // Texte avant le $...$
        if (match.index > lastIndex) {
            parts.push({ type: 'text', content: children.slice(lastIndex, match.index) });
        }
        // Fragment KaTeX
        parts.push({ type: 'math', content: match[1] });
        lastIndex = re.lastIndex;
    }
    // Texte restant après le dernier $...$
    if (lastIndex < children.length) {
        parts.push({ type: 'text', content: children.slice(lastIndex) });
    }

    return (
        <span className={className}>
            {parts.map((part, i) => {
                if (part.type === 'math') {
                    try {
                        const html = katex.renderToString(espacerMilliersTex(part.content), {
                            throwOnError: false,
                            displayMode: false,
                        });
                        return (
                            <span
                                key={i}
                                className="[&_.katex]:text-inherit expr-insecable"
                                dangerouslySetInnerHTML={{ __html: html }}
                            />
                        );
                    } catch {
                        return <span key={i}>${part.content}$</span>;
                    }
                }
                return <span key={i}>{espacerMilliersTexte(part.content)}</span>;
            })}
        </span>
    );
};

export default MathText;
